// YERİ: src/app/domains/website/core/seo.service.ts
// Vitrinin SEO etiketleri. SSR sayesinde bunlar HTML KAYNAĞINA girer — link
// önizlemesi (LinkedIn/Slack/WhatsApp) ve arama motorları JS çalıştırmadan görür.
// Client-render'da bu etiketler bot'a hiç ulaşmazdı; SSR'ın asıl gerekçesi bu.

import { DOCUMENT, PlatformLocation } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { WebPageAlternate } from './web.models';

/**
 * SSR'ın HTTP durum kodunu düzeltmesi için bileşenlerin bıraktığı işaret.
 *
 * Angular bir bileşenin içinden yanıt durumunu ayarlamaya izin vermez: render
 * bir Response döndüren kara kutudur. Ama render edilmiş HTML'i okuyabiliriz —
 * bu meta etiketi varsa server.ts yanıtı 404 olarak yeniden yazar.
 *
 * Bu şart: bulunamayan bir sayfaya 200 dönmek "soft 404"tür ve Google indeksini
 * var olmayan sayfalarla doldurur. SEO işinin tamamı bunu önlemek içindi.
 */
export const RENDER_STATUS_META = 'x-render-status';

/**
 * Kabuğa ait, sayfa değişiminde SİLİNMEYEN yapısal veri kimlikleri.
 * (WebLayout basar; cms-page'in temizliği bunlara dokunmaz.)
 */
const SITEWIDE_JSONLD_IDS = new Set(['ld-org']);

export interface SeoInput {
  title: string;
  description?: string | null;
  /** Sosyal paylaşım görseli — mutlak URL olmalı, bot'lar göreliyi çözmez. */
  imageUrl?: string | null;
  /** Bu sayfanın kanonik yolu ('/en/pricing'). */
  path: string;
  /**
   * Sayfa istenen dilde yoktu, varsayılan dilin içeriği çizildi.
   *
   * Bu hâlde canonical KENDİNE değil, içeriğin gerçekten ait olduğu dile
   * verilir ('/fr/pricing' → '/en/pricing'). Aksi hâlde Google iki ayrı URL'de
   * kelimesi kelimesine aynı metni görür, hangisini göstereceğini kendi seçer
   * ve ikisinin sıralamasını da bölerdi. hreflang de basılmaz — çevrilmemiş
   * bir dili "bu dilde sürüm var" diye ilan etmek hatalı hreflang raporu üretir.
   */
  fallbackPath?: string;
  languageCode: string;
  /** İçeriğin dili sağdan sola mı — <html dir> bunu izler. */
  rtl?: boolean;
  alternates?: WebPageAlternate[];
  type?: 'website' | 'article';
  /** 404 gibi indekslenmemesi gereken sayfalar için. */
  noIndex?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private titleSvc = inject(Title);
  private meta = inject(Meta);
  private doc = inject(DOCUMENT);
  // DOCUMENT.location SSR'da güvenilir değil; PlatformLocation sunucuda istek
  // URL'inden doğru doluyor (ServerPlatformLocation) ve tarayıcıda window.location'a
  // düşüyor. og:image / canonical MUTLAK olmak zorunda, o yüzden doğru origin şart.
  private platformLocation = inject(PlatformLocation);

  private readonly siteName = 'Melbarrel';

  apply(input: SeoInput): void {
    const title = input.title;
    const description = input.description ?? '';
    // Canonical, içeriğin GERÇEKTEN yayınlandığı URL'i gösterir: çeviri
    // yoksa varsayılan dilinkini.
    const url = this.absolute(input.fallbackPath ?? input.path);
    const image = input.imageUrl ? this.absolute(input.imageUrl) : '';

    this.titleSvc.setTitle(title);

    // lang ve dir, SEÇİLEN dilin değil ÇİZİLEN İÇERİĞİN dilinin: '/ar/pricing'
    // henüz çevrilmediyse ekranda İngilizce metin var ve onu sağdan sola
    // hizalamak metni okunmaz hâle getirirdi. Sayfa gerçekten Arapça'ya
    // çevrildiğinde languageCode 'ar' döner ve yön kendiliğinden döner.
    this.doc.documentElement.setAttribute('lang', input.languageCode);
    this.doc.documentElement.setAttribute('dir', input.rtl ? 'rtl' : 'ltr');

    this.setName('description', description);

    this.setProperty('og:title', title);
    this.setProperty('og:description', description);
    this.setProperty('og:url', url);
    this.setProperty('og:type', input.type ?? 'website');
    this.setProperty('og:site_name', this.siteName);
    this.setProperty('og:locale', input.languageCode);
    if (image) this.setProperty('og:image', image);

    // summary_large_image olmadan Twitter/X küçük bir küçük resim gösterir.
    this.setName('twitter:card', image ? 'summary_large_image' : 'summary');
    this.setName('twitter:title', title);
    this.setName('twitter:description', description);
    if (image) this.setName('twitter:image', image);

    // Her sayfada AÇIKÇA yazılır: SPA'da önceki sayfanın 'noindex'i kalırsa
    // sonraki (indekslenmesi gereken) sayfa da indeksten düşerdi.
    this.setName('robots', input.noIndex ? 'noindex, follow' : '');

    // SSR'ın HTTP durum kodunu 404'e çevirebilmesi için işaret bırak (aşağıya bak).
    this.setName(RENDER_STATUS_META, input.noIndex ? '404' : '');

    this.setCanonical(url);
    this.setAlternates(input);
  }

  // ── Kanonik + hreflang ────────────────────────────────────────────────────

  private setCanonical(url: string): void {
    this.upsertLink('canonical', { rel: 'canonical', href: url });
  }

  /**
   * hreflang yalnız GERÇEKTEN VAR OLAN diller için basılır (alternates backend'den
   * gelir). Çevrilmemiş bir dile hreflang vermek Google'a var olmayan sayfayı
   * göstermek olur ve o sayfa "hatalı hreflang" olarak raporlanır.
   */
  private setAlternates(input: SeoInput): void {
    // Önceki sayfadan kalan hreflang'ler temizlenmeli — SPA gezinmesinde
    // etiketler birikirdi ve yanlış sayfayı işaret ederdi.
    this.doc
      .querySelectorAll('link[rel="alternate"][data-seo="hreflang"]')
      .forEach((el) => el.remove());

    // Bu URL çevrilmemiş bir dilin varsayılan içeriği: kendisi bir dil sürümü
    // DEĞİL, canonical'ı başka sayfaya bakıyor. hreflang basmak Google'a
    // "bu Fransızca sürüm" demek olurdu — değil.
    if (input.fallbackPath) return;

    const all = [
      { languageCode: input.languageCode, slug: '' },
      ...(input.alternates ?? []),
    ];
    if (all.length < 2) return;

    const head = this.doc.head;

    for (const alt of input.alternates ?? []) {
      const link = this.doc.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', alt.languageCode);
      link.setAttribute('data-seo', 'hreflang');
      link.setAttribute('href', this.absolute(this.pathFor(alt.languageCode, alt.slug)));
      head.appendChild(link);
    }

    // x-default: dili eşleşmeyen ziyaretçi hangi sürüme gitmeli.
    const xDefault = this.doc.createElement('link');
    xDefault.setAttribute('rel', 'alternate');
    xDefault.setAttribute('hreflang', 'x-default');
    xDefault.setAttribute('data-seo', 'hreflang');
    xDefault.setAttribute('href', this.absolute(input.path));
    head.appendChild(xDefault);
  }

  private pathFor(lang: string, slug: string): string {
    return slug && slug !== 'home' ? `/${lang}/${slug}` : `/${lang}`;
  }

  // ── JSON-LD ───────────────────────────────────────────────────────────────

  /**
   * Yapısal veri. [innerHTML] KULLANILAMAZ — Angular'ın sanitizer'ı <script>
   * etiketini sessizce siler, JSON-LD hiç sayfaya girmez. Bu yüzden script
   * elemanı elle oluşturulup textContent'e yazılır.
   */
  setJsonLd(id: string, schema: object | null): void {
    const elementId = `ld-${id}`;
    this.doc.getElementById(elementId)?.remove();
    if (!schema) return;

    const script = this.doc.createElement('script');
    script.id = elementId;
    script.type = 'application/ld+json';
    // '<' escaped: the SSR serialiser writes script text raw, so a value holding
    // "</script>" would close the element and start markup. < is still valid JSON.
    script.textContent = JSON.stringify(schema).replace(/</g, '\\u003c');
    this.doc.head.appendChild(script);
  }

  /**
   * Sayfa değişirken önceki SAYFANIN şemaları kalmasın.
   *
   * Site geneli şemalar (Organization/WebSite/SoftwareApplication) KORUNUR —
   * onlar kabuğa aittir, sayfaya değil. Silinselerdi ilk gezinmede markanın
   * yapısal verisi kaybolur ve her sayfa kimliksiz kalırdı.
   */
  clearJsonLd(): void {
    this.doc
      .querySelectorAll('script[type="application/ld+json"][id^="ld-"]')
      .forEach((el) => {
        if (!SITEWIDE_JSONLD_IDS.has(el.id)) el.remove();
      });
  }

  // ── Yardımcılar ───────────────────────────────────────────────────────────

  private setName(name: string, content: string): void {
    if (content) this.meta.updateTag({ name, content });
    else this.meta.removeTag(`name="${name}"`);
  }

  private setProperty(property: string, content: string): void {
    if (content) this.meta.updateTag({ property, content });
    else this.meta.removeTag(`property="${property}"`);
  }

  private upsertLink(rel: string, attrs: Record<string, string>): void {
    let link = this.doc.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!link) {
      link = this.doc.createElement('link');
      this.doc.head.appendChild(link);
    }
    for (const [key, value] of Object.entries(attrs)) link.setAttribute(key, value);
  }

  /**
   * Göreli yolu mutlak URL'e çevirir. Bot'lar og:image ve canonical'da göreli
   * URL'i çözmez — mutlak olmak zorunda.
   */
  private absolute(pathOrUrl: string): string {
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;

    const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    return `${this.origin()}${path}`;
  }

  private origin(): string {
    const loc = this.platformLocation;
    if (!loc.hostname) return '';

    const protocol = loc.protocol || 'https:';
    // Varsayılan portlar URL'e yazılmaz — 'https://melbarrel.com:443' kanonik
    // olarak 'https://melbarrel.com'dan FARKLI sayılırdı.
    const isDefaultPort =
      !loc.port ||
      (protocol === 'https:' && loc.port === '443') ||
      (protocol === 'http:' && loc.port === '80');

    return `${protocol}//${loc.hostname}${isDefaultPort ? '' : `:${loc.port}`}`;
  }
}
